/**
 * Sprint 17 B7: DM Flow Builder — DM-specific journey canvas
 * Reuses JourneyCanvas with DM-only node types + compliance indicators.
 */
import { useState, useCallback } from "react";
import { ShieldCheck, Save } from "lucide-react";
import { JourneyCanvas } from "@/components/journey/journey-canvas";
import { useApiMutation } from "@/hooks/use-api";
import type { JourneyNode, JourneyEdge, JourneyNodeType } from "@/components/journey/types";

const DM_ALLOWED_TYPES: JourneyNodeType[] = [
  "trigger", "dm", "whatsapp", "sms", "wait", "condition", "split", "goal",
];

export default function DmFlowBuilderPage() {
  const [name, setName] = useState("");
  const [nodes, setNodes] = useState<JourneyNode[]>([]);
  const [edges, setEdges] = useState<JourneyEdge[]>([]);
  const [complianceVerified, setComplianceVerified] = useState(false);
  const { mutate: saveDmFlow, isLoading: isSaving } = useApiMutation<unknown, unknown>("/api/sequences/dm-flows");

  const handleSave = useCallback(async () => {
    if (!name.trim() || !complianceVerified) return;
    await saveDmFlow({ name: name.trim(), nodes, edges, complianceVerified });
  }, [name, nodes, edges, complianceVerified, saveDmFlow]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div className="space-y-2 flex-1 max-w-lg">
          <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">DM Flow Builder</h1>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Flow name..."
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          />
        </div>
        <div className="flex items-center gap-3">
          {/* Compliance check */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={complianceVerified}
              onChange={(e) => setComplianceVerified(e.target.checked)}
              className="rounded border-[var(--color-border)]"
            />
            <ShieldCheck className={`h-4 w-4 ${complianceVerified ? "text-[var(--color-success)]" : "text-[var(--color-muted-foreground)]"}`} />
            <span className="text-sm text-[var(--color-foreground)]">Opt-in verified</span>
          </label>

          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !name.trim() || !complianceVerified}
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {isSaving ? "Saving..." : "Save Flow"}
          </button>
        </div>
      </div>

      {!complianceVerified && (
        <div className="rounded-lg border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5 px-4 py-3 text-sm text-[var(--color-warning)]">
          You must verify opt-in compliance before activating this DM flow. Check the box above to confirm all contacts have given consent.
        </div>
      )}

      <JourneyCanvas
        nodes={nodes}
        edges={edges}
        onNodesChange={setNodes}
        onEdgesChange={setEdges}
        allowedNodeTypes={DM_ALLOWED_TYPES}
      />
    </div>
  );
}
